import { VersionCompareRow } from 'components/v2/compiler/version-compare/VersionCompareRow';
import { EpdVersionHistoryData } from 'components/v2/compiler/version-history/utils/EpdVersionHistoryDataContext';
import SmartLabel from 'components/v2/labels/SmartLabel';
import React from 'react';
import { FieldPanelHeader } from 'styles/v2/Styles.styled';
import ComparePanelComponent from '../../../../pages/v2/compiler/ComparePanelComponent';
import DynamicGridPdf from '../grids/lca-results/DynamicGridPdf';
import FixedGridPdf from '../grids/lca-results/FixedGridPdf';
import { isCpcrPerformanceTableFilledIn } from '../version-history/content/VersionHistoryTechnicalPerformanceSection';
import { mergeToLeftRightByIndex } from './utils/mergers';

const CompareTechnicalPerformanceSection: React.FC<{
  data: { left: EpdVersionHistoryData | null; right: EpdVersionHistoryData | null };
}> = ({ data }) => {
  const anyTechnicalPerformanceLeft = data.left?.cpcrSpecificData?.tables.some((t) =>
    isCpcrPerformanceTableFilledIn(data.left!.cpcrSpecificData, t)
  );
  const anyTechnicalPerformanceRight = data.right?.cpcrSpecificData?.tables.some((t) =>
    isCpcrPerformanceTableFilledIn(data.right!.cpcrSpecificData, t)
  );

  if (!anyTechnicalPerformanceLeft && !anyTechnicalPerformanceRight) return null;

  const mergedTechnicalPerformanceTables = mergeToLeftRightByIndex(
    data.left?.cpcrSpecificData?.tables,
    data.right?.cpcrSpecificData?.tables
  );

  return (
    <ComparePanelComponent>
      {anyTechnicalPerformanceLeft ? (
        <FieldPanelHeader>Technical characteristics and performance</FieldPanelHeader>
      ) : (
        <div />
      )}
      {anyTechnicalPerformanceRight ? (
        <FieldPanelHeader>Technical characteristics and performance</FieldPanelHeader>
      ) : (
        <div />
      )}

      {mergedTechnicalPerformanceTables.map((pair) => {
        const leftTableCode = pair.left?.tableCode;
        const rightTableCode = pair.right?.tableCode;

        const leftCpcrSpecificData = data.left?.cpcrSpecificData;
        const rightCpcrSpecificData = data.right?.cpcrSpecificData;

        const extendedPair = {
          left: { ...pair.left, cpcrSpecificData: leftCpcrSpecificData },
          right: { ...pair.right, cpcrSpecificData: rightCpcrSpecificData },
        };

        const hasRelevantData = (x?: any) => {
          if (!x?.cpcrSpecificData?.data) return false;
          return x.cpcrSpecificData.data
            .filter((y: any) => y.tableCode === x.tableCode)
            .some((i: any) => i.a1 || i.a2 || i.a3);
        };

        return (
          <VersionCompareRow
            key={`${leftTableCode ?? ''}_${rightTableCode ?? ''}`}
            data={extendedPair}
            displayCondition={hasRelevantData}
            compareByDom
          >
            {({ item: t, isLeft, compareClassName }) => {
              return (
                <React.Fragment key={(isLeft ? 'left_' : 'right_') + t.tableCode}>
                  {t.isFixed ? (
                    <div>
                      <SmartLabel label={t.tableTitle} />
                      <FixedGridPdf
                        className={compareClassName}
                        columns={t.cpcrSpecificData?.columns.filter((x) => x.tableCode == t.tableCode)}
                        value={t.cpcrSpecificData?.data.filter((x) => x.tableCode == t.tableCode) || []}
                      />
                    </div>
                  ) : (
                    (t.cpcrSpecificData?.data.filter((x) => x.tableCode == t.tableCode) || []).length > 0 && (
                      <div>
                        <SmartLabel label={t.tableTitle} />
                        <DynamicGridPdf
                          className={compareClassName}
                          columns={t.cpcrSpecificData?.columns.filter((x) => x.tableCode == t.tableCode)}
                          value={t.cpcrSpecificData?.data.filter((x) => x.tableCode == t.tableCode) || []}
                        />
                      </div>
                    )
                  )}
                </React.Fragment>
              );
            }}
          </VersionCompareRow>
        );
      })}
    </ComparePanelComponent>
  );
};

export default CompareTechnicalPerformanceSection;
